'use client';

import {
  HyperLinkButtonBase,
  NavigationBar,
  PillBar,
} from '@bts-web/core-features/common';
import { css } from '@bts-web/utils-style-engine';
import { useModalDrawerContext } from '../../common/components/ModalDrawerContext/ModalDrawerContext';
import { StopOrderInfoDrawer } from '../components/StopOrderInfoDrawer/StopOrderInfoDrawer';
import { NavigationBackButton } from '@bts-web/core-features/navigation';
import { RegularInfo } from '../../common/svgIcons';
import { OrdersListTranslations } from '../utils/getOrdersListTranslations';
import { useOrdersContext } from './OrdersContext.use-client';
import { OrderStatus } from '@bts-web/data-layer/server';
import { HiddenDesktop } from '../../layouts/LayoutWithLeftDesktopNav/Reusable/HiddenDesktop/HiddenDesktop';
import { HiddenMobile } from '../../layouts/LayoutWithLeftDesktopNav/Reusable/HiddenMobile/HiddenMobile';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { TradePriceType } from '../types';
import { useIsBelowScreenWidth } from '@bts-web/core-features/layouts';

interface OrdersListHeaderProps {
  filterItem: { label: string; value: string }[];
  translations: OrdersListTranslations;
}

const orderButtonStyles = css({
  width: { base: 'auto', lg: '200px' },
  display: 'flex',
  flex: {
    base: '1',
    lg: 'none',
  },
});

const regularInfoStyle = css({
  cursor: 'pointer',
  width: { base: 'medium', lg: 'medium' },
  height: { base: 'medium', lg: 'medium' },
});

export const OrdersListHeader = ({
  filterItem,
  translations,
}: OrdersListHeaderProps) => {
  const { openDrawer } = useModalDrawerContext();

  const isMobile = useIsBelowScreenWidth(1024);

  const ordersContext = useOrdersContext();

  const buyUrl = `${NEXT_ROUTES.DISCOVER}?next=order`;

  const sellUrl = `${NEXT_ROUTES.MY_ASSETS}?priceType=${TradePriceType.STOP}`;

  return (
    <>
      <HiddenDesktop>
        <NavigationBar
          title={translations.stopOrderTitle}
          NavigationLeftSlot={<NavigationBackButton />}
          NavigationRightSlot={
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              })}
            >
              <RegularInfo
                className={regularInfoStyle}
                onClick={() => {
                  openDrawer(StopOrderInfoDrawer, {
                    translations,
                    title: translations.stopOrderTitle,
                    position: 'bottom',
                  });
                }}
              />
            </div>
          }
        />
      </HiddenDesktop>

      <div
        className={css({
          backgroundColor: 'screen_background.primary',
          paddingY: 'small',
          paddingX: 'medium',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        })}
      >
        <PillBar
          defaultValue={ordersContext.selectedFilter}
          disabled={ordersContext.isPending}
          items={filterItem}
          onValueChange={(value) => {
            ordersContext.setSelectedFilter(value as OrderStatus);
          }}
        />

        <div
          className={css({
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 'extra_small',
          })}
        >
          <div
            className={css({
              position: {
                base: 'fixed',
                lg: 'static',
              },
              bottom: { base: '16px', lg: 'unset' },
              left: { base: '0', lg: 'unset' },
              right: { base: '0', lg: 'unset' },
              display: 'flex',
              flexDirection: 'row',
              height: 'extra_large_3',
              padding: { base: 'extra_small', lg: 'extra_small' },
              gap: 'extra_small',
              justifyContent: 'center',
              alignItems: 'center',
              flex: '1 0 0',
            })}
          >
            <HyperLinkButtonBase
              to={buyUrl}
              visual="primary"
              size={isMobile ? 'large' : 'small'}
              className={orderButtonStyles}
            >
              {translations.stopOrderBuyTitle}
            </HyperLinkButtonBase>

            <HyperLinkButtonBase
              to={sellUrl}
              visual="secondary"
              size={isMobile ? 'large' : 'small'}
              className={orderButtonStyles}
            >
              {translations.stopOrderSellTitle}
            </HyperLinkButtonBase>
          </div>

          <HiddenMobile>
            <RegularInfo
              className={regularInfoStyle}
              onClick={() => {
                openDrawer(StopOrderInfoDrawer, {
                  translations,
                  title: translations.stopOrderTitle,
                  position: 'right',
                });
              }}
            />
          </HiddenMobile>
        </div>
      </div>
    </>
  );
};
